<template>
  <div class="space-y-6">
    <!-- Header -->
    <div>
      <h1 class="text-3xl font-bold text-gray-900">Settings</h1>
      <p class="text-gray-500 mt-1">Manage your account and system preferences</p>
    </div>
    
    <!-- Settings Navigation -->
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <!-- Sidebar -->
      <div class="lg:col-span-1">
        <nav class="space-y-1">
          <button
            v-for="section in settingsSections"
            :key="section.id"
            @click="activeSection = section.id"
            :class="[
              'w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors',
              activeSection === section.id
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-700 hover:bg-gray-100'
            ]"
          >
            <component :is="section.icon" class="w-5 h-5 mr-3" />
            {{ section.name }}
          </button>
        </nav>
      </div>
      
      <!-- Content -->
      <div class="lg:col-span-3">
        <!-- Profile Settings -->
        <div v-if="activeSection === 'profile'" class="card">
          <div class="card-header">
            <h2 class="text-xl font-semibold">Profile Settings</h2>
          </div>
          <div class="card-body space-y-6">
            <!-- Avatar -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
              <div class="flex items-center space-x-4">
                <div class="w-20 h-20 bg-gray-300 rounded-full"></div>
                <div>
                  <button class="btn btn-primary btn-sm">Upload New</button>
                  <p class="text-xs text-gray-500 mt-1">JPG, PNG or GIF. Max 2MB.</p>
                </div>
              </div>
            </div>
            
            <!-- Personal Info -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input v-model="profileForm.firstName" type="text" class="input w-full" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input v-model="profileForm.lastName" type="text" class="input w-full" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input v-model="profileForm.email" type="email" class="input w-full" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input v-model="profileForm.phone" type="tel" class="input w-full" />
              </div>
            </div>
            
            <!-- Timezone -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              <select v-model="profileForm.timezone" class="input w-full">
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
              </select>
            </div>
            
            <div class="flex justify-end">
              <button @click="saveProfile" :disabled="loading" class="btn btn-primary">
                <span v-if="loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                {{ loading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </div>
        </div>
        
        <!-- Notification Settings -->
        <div v-if="activeSection === 'notifications'" class="card">
          <div class="card-header">
            <h2 class="text-xl font-semibold">Notification Preferences</h2>
          </div>
          <div class="card-body space-y-6">
            <!-- Email Notifications -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">Email Notifications</h3>
              <div class="space-y-3">
                <label class="flex items-center">
                  <input v-model="notifications.email.alerts" type="checkbox" class="mr-3" />
                  <div>
                    <div class="font-medium">Critical Alerts</div>
                    <div class="text-sm text-gray-500">Receive emails for critical health alerts</div>
                  </div>
                </label>
                <label class="flex items-center">
                  <input v-model="notifications.email.reports" type="checkbox" class="mr-3" />
                  <div>
                    <div class="font-medium">Weekly Reports</div>
                    <div class="text-sm text-gray-500">Get weekly wellness summaries</div>
                  </div>
                </label>
                <label class="flex items-center">
                  <input v-model="notifications.email.reminders" type="checkbox" class="mr-3" />
                  <div>
                    <div class="font-medium">Call Reminders</div>
                    <div class="text-sm text-gray-500">Reminder before scheduled calls</div>
                  </div>
                </label>
              </div>
            </div>
            
            <!-- Push Notifications -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">Push Notifications</h3>
              <div class="space-y-3">
                <label class="flex items-center">
                  <input v-model="notifications.push.enabled" type="checkbox" class="mr-3" />
                  <div>
                    <div class="font-medium">Enable Push Notifications</div>
                    <div class="text-sm text-gray-500">Receive browser notifications</div>
                  </div>
                </label>
                <label class="flex items-center">
                  <input v-model="notifications.push.sound" type="checkbox" class="mr-3" />
                  <div>
                    <div class="font-medium">Sound Alerts</div>
                    <div class="text-sm text-gray-500">Play sound for important notifications</div>
                  </div>
                </label>
              </div>
            </div>
            
            <!-- Alert Thresholds -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">Alert Thresholds</h3>
              <div class="space-y-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Wellness Score Alert (below)
                  </label>
                  <input v-model="notifications.thresholds.wellness" type="number" class="input w-32" min="0" max="100" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Missed Calls Alert (after)
                  </label>
                  <input v-model="notifications.thresholds.missedCalls" type="number" class="input w-32" min="1" />
                </div>
              </div>
            </div>
            
            <div class="flex justify-end">
              <button @click="saveNotifications" :disabled="loading" class="btn btn-primary">
                <span v-if="loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                {{ loading ? 'Saving...' : 'Save Preferences' }}
              </button>
            </div>
          </div>
        </div>
        
        <!-- Call Settings -->
        <div v-if="activeSection === 'calls'" class="card">
          <div class="card-header">
            <h2 class="text-xl font-semibold">Call Settings</h2>
          </div>
          <div class="card-body space-y-6">
            <!-- Default Call Settings -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">Default Call Preferences</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Default Call Duration</label>
                  <select v-model="callSettings.defaultDuration" class="input w-full">
                    <option value="5">5 minutes</option>
                    <option value="10">10 minutes</option>
                    <option value="15">15 minutes</option>
                    <option value="20">20 minutes</option>
                    <option value="30">30 minutes</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Default Call Time</label>
                  <input v-model="callSettings.defaultTime" type="time" class="input w-full" />
                </div>
              </div>
            </div>
            
            <!-- AI Voice Settings -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">AI Voice Settings</h3>
              <div class="space-y-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Voice Type</label>
                  <select v-model="callSettings.voiceType" class="input w-full">
                    <option value="sarah">Sarah (Female, Warm)</option>
                    <option value="john">John (Male, Friendly)</option>
                    <option value="emma">Emma (Female, Professional)</option>
                    <option value="david">David (Male, Calm)</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Speaking Speed</label>
                  <div class="flex items-center space-x-4">
                    <span class="text-sm">Slow</span>
                    <input v-model="callSettings.speakingSpeed" type="range" min="0.5" max="1.5" step="0.1" class="flex-1" />
                    <span class="text-sm">Fast</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Recording Settings -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">Recording Settings</h3>
              <div class="space-y-3">
                <label class="flex items-center">
                  <input v-model="callSettings.recordCalls" type="checkbox" class="mr-3" />
                  <div>
                    <div class="font-medium">Record All Calls</div>
                    <div class="text-sm text-gray-500">Store call recordings for review</div>
                  </div>
                </label>
                <label class="flex items-center">
                  <input v-model="callSettings.transcribeCalls" type="checkbox" class="mr-3" />
                  <div>
                    <div class="font-medium">Generate Transcripts</div>
                    <div class="text-sm text-gray-500">Create text transcripts of all calls</div>
                  </div>
                </label>
              </div>
            </div>
            
            <div class="flex justify-end">
              <button @click="saveCallSettings" :disabled="loading" class="btn btn-primary">
                <span v-if="loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                {{ loading ? 'Saving...' : 'Save Settings' }}
              </button>
            </div>
          </div>
        </div>
        
        <!-- Security Settings -->
        <div v-if="activeSection === 'security'" class="card">
          <div class="card-header">
            <h2 class="text-xl font-semibold">Security Settings</h2>
          </div>
          <div class="card-body space-y-6">
            <!-- Change Password -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">Change Password</h3>
              <div class="space-y-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <input v-model="security.currentPassword" type="password" class="input w-full" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input v-model="security.newPassword" type="password" class="input w-full" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <input v-model="security.confirmPassword" type="password" class="input w-full" />
                </div>
                <button @click="changePassword" :disabled="loading" class="btn btn-primary">
                  <span v-if="loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  {{ loading ? 'Updating...' : 'Update Password' }}
                </button>
              </div>
            </div>
            
            <!-- Two-Factor Authentication -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">Two-Factor Authentication</h3>
              <div class="p-4 bg-gray-50 rounded-lg">
                <div class="flex items-center justify-between">
                  <div>
                    <div class="font-medium">Two-Factor Authentication</div>
                    <div class="text-sm text-gray-500">Add an extra layer of security to your account</div>
                  </div>
                  <button class="btn btn-secondary">
                    {{ security.twoFactorEnabled ? 'Disable' : 'Enable' }}
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Active Sessions -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">Active Sessions</h3>
              <div class="space-y-2">
                <div
                  v-for="session in activeSessions"
                  :key="session.id"
                  class="flex items-center justify-between p-3 bg-gray-50 rounded"
                >
                  <div>
                    <div class="font-medium text-sm">{{ session.device }}</div>
                    <div class="text-xs text-gray-500">{{ session.location }} · {{ session.lastActive }}</div>
                  </div>
                  <button
                    v-if="!session.current"
                    @click="revokeSession(session.id)"
                    class="btn btn-secondary btn-sm"
                  >
                    Revoke
                  </button>
                  <span v-else class="badge badge-success">Current</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- System Settings -->
        <div v-if="activeSection === 'system'" class="card">
          <div class="card-header">
            <h2 class="text-xl font-semibold">System Settings</h2>
          </div>
          <div class="card-body space-y-6">
            <!-- Data Management -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">Data Management</h3>
              <div class="space-y-3">
                <div class="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <div class="font-medium">Export Data</div>
                    <div class="text-sm text-gray-500">Download all your data in JSON format</div>
                  </div>
                  <button class="btn btn-secondary">Export</button>
                </div>
                <div class="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <div class="font-medium">Clear Cache</div>
                    <div class="text-sm text-gray-500">Clear temporary files and cached data</div>
                  </div>
                  <button class="btn btn-secondary">Clear</button>
                </div>
              </div>
            </div>
            
            <!-- API Settings -->
            <div>
              <h3 class="font-medium text-gray-900 mb-3">API Configuration</h3>
              <div class="space-y-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                  <div class="flex space-x-2">
                    <input
                      :value="system.apiKey"
                      :type="showApiKey ? 'text' : 'password'"
                      class="input flex-1"
                      readonly
                    />
                    <button
                      @click="showApiKey = !showApiKey"
                      class="btn btn-secondary"
                    >
                      {{ showApiKey ? 'Hide' : 'Show' }}
                    </button>
                    <button class="btn btn-secondary">Regenerate</button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Danger Zone -->
            <div>
              <h3 class="font-medium text-danger-600 mb-3">Danger Zone</h3>
              <div class="p-4 border-2 border-danger-200 rounded-lg bg-danger-50">
                <div class="flex items-center justify-between">
                  <div>
                    <div class="font-medium text-danger-900">Delete Account</div>
                    <div class="text-sm text-danger-700">Permanently delete your account and all data</div>
                  </div>
                  <button class="btn btn-danger">Delete Account</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import api from '@/api/axios'
import {
  UserIcon,
  BellIcon,
  PhoneIcon,
  LockClosedIcon,
  CogIcon
} from '@heroicons/vue/24/outline'

const toast = useToast()
const authStore = useAuthStore()

const activeSection = ref('profile')

const settingsSections = [
  { id: 'profile', name: 'Profile', icon: UserIcon },
  { id: 'notifications', name: 'Notifications', icon: BellIcon },
  { id: 'calls', name: 'Call Settings', icon: PhoneIcon },
  { id: 'security', name: 'Security', icon: LockClosedIcon },
  { id: 'system', name: 'System', icon: CogIcon }
]

// Use real user data from auth store
const profile = computed(() => authStore.user || {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  timezone: 'America/New_York'
})

// Local editable copy for form
const profileForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  timezone: 'America/New_York'
})

// Initialize form with current user data
function initializeProfile() {
  if (authStore.user) {
    profileForm.value = {
      firstName: authStore.user.first_name || '',
      lastName: authStore.user.last_name || '', 
      email: authStore.user.email || '',
      phone: authStore.user.phone || '',
      timezone: authStore.user.timezone || 'America/New_York'
    }
  }
}

const notifications = ref({
  email: {
    alerts: true,
    reports: true,
    reminders: true
  },
  push: {
    enabled: false,
    sound: true
  },
  thresholds: {
    wellness: 60,
    missedCalls: 2
  }
})

const callSettings = ref({
  defaultDuration: 15,
  defaultTime: '10:00',
  voiceType: 'sarah',
  speakingSpeed: 1.0,
  recordCalls: true,
  transcribeCalls: true
})

const security = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
  twoFactorEnabled: false
})

const system = ref({
  apiKey: ''
})

// Load user settings from API
async function loadSettings() {
  try {
    const [notificationResponse, callResponse, systemResponse] = await Promise.all([
      api.get('/auth/notifications').catch(() => ({ data: {} })),
      api.get('/auth/call-settings').catch(() => ({ data: {} })),
      api.get('/auth/system-settings').catch(() => ({ data: {} }))
    ])
    
    // Update settings with API data or keep defaults
    if (notificationResponse.data) {
      Object.assign(notifications.value, notificationResponse.data)
    }
    if (callResponse.data) {
      Object.assign(callSettings.value, callResponse.data)
    }
    if (systemResponse.data?.apiKey) {
      system.value.apiKey = systemResponse.data.apiKey
    }
  } catch (error) {
    console.error('Failed to load some settings:', error)
  }
}

const showApiKey = ref(false)

const activeSessions = ref([])
const loading = ref(false)

async function saveProfile() {
  if (loading.value) return
  loading.value = true
  
  try {
    const response = await api.put('/auth/profile', {
      first_name: profileForm.value.firstName,
      last_name: profileForm.value.lastName,
      email: profileForm.value.email,
      phone: profileForm.value.phone,
      timezone: profileForm.value.timezone
    })
    
    // Update auth store with new user data
    await authStore.fetchUser()
    toast.success('Profile updated successfully!')
  } catch (error) {
    toast.error(error.response?.data?.error || 'Failed to update profile')
  } finally {
    loading.value = false
  }
}

async function saveNotifications() {
  if (loading.value) return
  loading.value = true
  
  try {
    await api.put('/auth/notifications', notifications.value)
    toast.success('Notification preferences saved!')
  } catch (error) {
    toast.error(error.response?.data?.error || 'Failed to save notification preferences')
  } finally {
    loading.value = false
  }
}

async function saveCallSettings() {
  if (loading.value) return
  loading.value = true
  
  try {
    await api.put('/auth/call-settings', callSettings.value)
    toast.success('Call settings updated!')
  } catch (error) {
    toast.error(error.response?.data?.error || 'Failed to save call settings')
  } finally {
    loading.value = false
  }
}

async function changePassword() {
  if (security.value.newPassword !== security.value.confirmPassword) {
    toast.error('Passwords do not match')
    return
  }
  
  if (loading.value) return
  loading.value = true
  
  try {
    await api.put('/auth/password', {
      current_password: security.value.currentPassword,
      new_password: security.value.newPassword
    })
    
    toast.success('Password changed successfully!')
    security.value = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      twoFactorEnabled: security.value.twoFactorEnabled
    }
  } catch (error) {
    toast.error(error.response?.data?.error || 'Failed to change password')
  } finally {
    loading.value = false
  }
}

async function fetchActiveSessions() {
  try {
    const response = await api.get('/auth/sessions')
    activeSessions.value = response.data.sessions || []
  } catch (error) {
    console.error('Failed to fetch active sessions:', error)
    activeSessions.value = []
  }
}

async function revokeSession(sessionId) {
  try {
    await api.delete(`/auth/sessions/${sessionId}`)
    activeSessions.value = activeSessions.value.filter(s => s.id !== sessionId)
    toast.success('Session revoked successfully')
  } catch (error) {
    toast.error('Failed to revoke session')
  }
}

onMounted(() => {
  initializeProfile()
  loadSettings()
  fetchActiveSessions()
})
</script>